/**
 * Hold UI — Step 56, reshaped on Kai's feedback: the STATUS DROPDOWN is the one
 * way in and out. Selecting "On hold" opens this modal for the required fields
 * (the API refuses a bare status change to on_hold); selecting any other
 * status releases the hold server-side. The held card is pure information —
 * no second button competing with the dropdown.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PauseCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Modal } from '../../components/ui/modal';
import { Select } from '../../components/ui/select';
import { fullTime, relativeTime } from '../../lib/time';
import { qk } from '../../lib/queryClient';
import { ticketsApi, type HoldInfo } from '../../features/tickets/api';

export function HeldCard({ activeHold }: { activeHold: HoldInfo }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
        <PauseCircle className="h-3.5 w-3.5" /> On hold — {activeHold.reasonLabel}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {activeHold.until
          ? `Auto-reopens ${relativeTime(activeHold.until)} (${fullTime(activeHold.until)})`
          : 'Held until someone changes the status.'}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Change the status to release it. SLA clocks are paused.
      </p>
    </div>
  );
}

export function HoldModal({
  ticketId,
  open,
  onClose,
}: {
  ticketId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reasonId, setReasonId] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: reasons } = useQuery({
    queryKey: qk.holdReasons,
    queryFn: ticketsApi.holdReasons,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const hold = useMutation({
    mutationFn: () =>
      ticketsApi.hold(ticketId, { reasonId, ...(until ? { until: new Date(until).getTime() } : {}) }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error);
      setUntil('');
      setReasonId('');
      setError(null);
      onClose();
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Put on hold">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Reason (required)</label>
          <Select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
            <option value="">Choose a reason…</option>
            {(reasons?.entries ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Until (leave empty to hold indefinitely)
          </label>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:[color-scheme:dark]"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            When it passes, the ticket reopens itself and the thread says why. SLA clocks pause while held.
          </p>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => hold.mutate()} disabled={!reasonId || hold.isPending}>
            {hold.isPending ? 'Holding…' : 'Put on hold'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
